import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useFormatCurrency, useMobile } from "@krea/customer-features/hooks";
import { settings } from "@krea/customer-features/settings";
import BidBenefits from "@krea/customer-features/shared-components/bid-dashboards/commons/BidBenefits";
import BidItem from "@krea/customer-features/shared-components/bid-dashboards/commons/BidItem";
import BidsListItemDesktop from "@krea/customer-features/shared-components/bid-dashboards/commons/BidsListItemDesktop";
import BidsListItemDetails from "@krea/customer-features/shared-components/bid-dashboards/commons/BidsListItemDetails";
import BidsListItemMobile from "@krea/customer-features/shared-components/bid-dashboards/commons/BidsListItemMobile";
import BidsListTableTD from "@krea/customer-features/shared-components/bid-dashboards/commons/BidsListTableTD";
import BidDetails from "@krea/customer-features/shared-components/bid-details/BidDetails";
import { ILeasingBid } from "@krea/customer-features/types/lenderResponse";
import { BID_TYPE, getBenefits } from "@krea/customer-features/utils";

import getUnsubmittedFieldRequests from "../commons/utils";
import { ILoanBidItem } from "../company-loan-bids/CompanyLoanBidItem";

interface ILeasingBidItem extends ILoanBidItem {
  bid: ILeasingBid;
}

const LeasingBidItem = ({
  onSelectLenderResponseClick,
  isSelecting,
  bid,
  selectedLenderResponseID,
  rateKey,
  isRecommended,
  isGoodAlternative,
  isPaidOutPage,
}: ILeasingBidItem) => {
  const { t } = useTranslation();
  const isMobile = useMobile();
  const [details, setDetails] = useState(false);
  const [formattedCurrencyValue] = useFormatCurrency({
    currency: settings.currency,
  });

  const {
    amount,
    monthlyPayment,
    paybackTimeMonths,
    totalCost,
    lenderResponse,
  } = bid;
  const { benefits, lenderApplication, fieldRequests = [] } = lenderResponse;
  const { lenderCompany } = lenderApplication;

  const isPaidOut = !!lenderResponse.paidOutAt;
  const isSelected = lenderResponse.id === selectedLenderResponseID;
  const detailsButton = details ? t("dash.hideDetails") : t("dash.showDetails");
  const benefitsList = getBenefits(benefits);
  const unSubmittedFieldRequests = getUnsubmittedFieldRequests(fieldRequests);

  const bidDetailsComponent = (
    <BidsListItemDetails
      lenderResponse={lenderResponse}
      lenderCompany={lenderCompany}
      details={<BidDetails bid={bid} bidType={BID_TYPE.LEASING} />}
    />
  );

  const getPaybackTimeValue = () => {
    if (paybackTimeMonths > 1) {
      return `${paybackTimeMonths} ${t("dash.loanBids.paybackTimeMonthsFormat")}`;
    }

    return `${paybackTimeMonths} ${t("dash.loanBids.paybackTimeMonthFormat")}`;
  };

  const openDetails = () => {
    setDetails(!details);
  };

  return (
    <BidItem
      toggleDetails={openDetails}
      chooseBid={() =>
        onSelectLenderResponseClick(lenderResponse, unSubmittedFieldRequests)
      }
      isSelecting={isSelecting}
    >
      {!isMobile ? (
        <BidsListItemDesktop
          showDetails={details}
          isSelected={isSelected}
          isRecommended={isRecommended}
          isGoodAlternative={isGoodAlternative}
          isPaidOut={isPaidOut}
          lenderCompany={lenderCompany}
          lenderResponse={lenderResponse}
          detailsButtonText={detailsButton}
          additionalList={
            <BidBenefits benefits={benefitsList} isCondensedVersion />
          }
          bidDetails={bidDetailsComponent}
          isPaidOutPage={isPaidOutPage}
        >
          <BidsListTableTD
            isText
            scrollable
            heading={t("dash.leasingBids.columns.amount.title")}
          >
            {formattedCurrencyValue(amount)}
          </BidsListTableTD>

          <BidsListTableTD
            isText
            heading={t("dash.leasingBids.columns.repaymentTime.title")}
          >
            {getPaybackTimeValue()}
          </BidsListTableTD>

          {/* <BidsListTableTD
            isText
            heading={t(`dash.commonBidsColumns.${rateKey}.title`)}
          >
            {bid[rateKey]}%
          </BidsListTableTD> */}

          <BidsListTableTD
            isText
            scrollable
            heading={t("dash.leasingBids.columns.monthlyCost.title")}
          >
            {formattedCurrencyValue(monthlyPayment)}
          </BidsListTableTD>

          {/* <BidsListTableTD
            isText
            scrollable
            heading={t('dash.leasingBids.columns.totalCost.title')}
          >
            {formattedCurrencyValue(totalCost)}
          </BidsListTableTD> */}
        </BidsListItemDesktop>
      ) : (
        <BidsListItemMobile
          isLeasing
          amount={formattedCurrencyValue(amount)}
          paybackTime={getPaybackTimeValue()}
          monthlyCost={formattedCurrencyValue(monthlyPayment)}
          showDetails={details}
          isSelected={isSelected}
          isRecommended={isRecommended}
          isGoodAlternative={isGoodAlternative}
          isPaidOut={isPaidOut}
          lenderCompany={lenderCompany}
          lenderResponse={lenderResponse}
          detailsButtonText={detailsButton}
          bidDetails={bidDetailsComponent}
          isPaidOutPage={isPaidOutPage}
        >
          {/* <BidsListItemMobileOtherInfo
            title={t(`dash.commonBidsColumns.${rateKey}.title`)}
            value={`${bid[rateKey]}%`}
          /> */}

          {/* <BidsListItemMobileOtherInfo
            title={t('dash.leasingBids.columns.totalCost.title')}
            value={formattedCurrencyValue(totalCost)}
          /> */}

          {benefitsList && (
            <div className="mt-3">
              <BidBenefits benefits={benefitsList} isCondensedVersion />
            </div>
          )}
        </BidsListItemMobile>
      )}
    </BidItem>
  );
};

export default LeasingBidItem;
